import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import BuildIcon from "@mui/icons-material/Build";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { AdminContext } from "../../../contexts/AdminContext";
import PatchingTemplateFormDialog from "./PatchingTemplateFormDialog";
import PatchingScheduleFormDialog from "./PatchingScheduleFormDialog";
import DeleteTemplateDialog from "./PatchingTemplateDeleteDialog";
import DeleteScheduleDialog from "./PatchingSchedulesDeleteDialog";

const TEMPLATE_DRAWER_WIDTH = 340;
const SCHEDULE_ROWS_PER_PAGE_OPTIONS = [5, 10, 20];
const DEFAULT_SCHEDULE_ROWS_PER_PAGE = 5;
const SCHEDULE_STATUSES = {
  planned: { label: "Planned", color: "primary" },
  in_progress: { label: "In progress", color: "warning" },
  completed: { label: "Completed", color: "success" },
  cancelled: { label: "Cancelled", color: "default" },
};
const ScheduleStatus = ({ status }) => (
  <Chip
    size='small'
    label={SCHEDULE_STATUSES[status]?.label || status}
    color={SCHEDULE_STATUSES[status]?.color || "default"}
    variant='outlined'
    sx={{ fontWeight: 600 }}
  />
);

const PaperCustom = ({ children, sx, ...props }) => (
  <Box
    sx={{
      bgcolor: "background.paper",
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

function extractList(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  return [];
}

function extractPaginatedSchedules(res) {
  const payload = res?.data ?? res;
  if (Array.isArray(payload)) {
    return {
      items: payload,
      total: payload.length,
      page: 1,
      limit: payload.length,
    };
  }
  const items = Array.isArray(payload?.schedules)
    ? payload.schedules
    : Array.isArray(payload?.data)
      ? payload.data
      : [];
  return {
    items,
    total: Number(payload?.total ?? items.length),
    page: Number(payload?.page ?? 1),
    limit: Number(payload?.limit ?? items.length),
  };
}

function formatScheduleDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // Format: DD/MM/YYYY, HH:mm:ss
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function truncate(str, max) {
  if (!str) return "";
  const s = String(str);
  return s.length <= max ? s : `${s.slice(0, max)}…`;
}

function scheduleBuckets(schedules) {
  const now = Date.now();
  const live = [];
  const upcoming = [];
  const closed = [];

  const sorted = [...schedules].sort((a, b) => {
    const ta = new Date(a.scheduled_start_time || 0).getTime();
    const tb = new Date(b.scheduled_start_time || 0).getTime();
    return ta - tb;
  });

  sorted.forEach((sch) => {
    const start = new Date(sch.scheduled_start_time || 0).getTime();
    const end = sch.scheduled_end_time
      ? new Date(sch.scheduled_end_time).getTime()
      : null;
    const st = sch.status;

    if (st === "completed" || st === "cancelled") {
      closed.push(sch);
      return;
    }
    if (st === "in_progress") {
      live.push(sch);
      return;
    }
    if (st === "planned") {
      if (start > now) {
        upcoming.push(sch);
        return;
      }
      if (start <= now && (end == null || end >= now)) {
        live.push(sch);
        return;
      }
      closed.push(sch);
      return;
    }
    closed.push(sch);
  });

  return { live, upcoming, closed };
}

const PatchingCenterConsole = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const {
    patchingTemplateServices: {
      listPatchingTemplates,
      createPatchingTemplate,
      updatePatchingTemplate,
      deletePatchingTemplate,
      listPatchingSchedules,
      createPatchingSchedule,
      updatePatchingSchedule,
      deletePatchingSchedule,
    },
  } = useContext(AdminContext);

  const [rows, setRows] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [scheduleStatus, setScheduleStatus] = useState("all");
  const [schedulesError, setSchedulesError] = useState("");
  const scheduleRequestId = useRef(0);
  const [scheduleTotal, setScheduleTotal] = useState(0);
  const [schedulePage, setSchedulePage] = useState(0);
  const [scheduleRowsPerPage, setScheduleRowsPerPage] = useState(
    DEFAULT_SCHEDULE_ROWS_PER_PAGE
  );
  const [loading, setLoading] = useState(true);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [filterCode, setFilterCode] = useState("");
  const [filterActive, setFilterActive] = useState("all");
  const [snack, setSnack] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [scheduleFormOpen, setScheduleFormOpen] = useState(false);
  const [scheduleMode, setScheduleMode] = useState("create");
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleLockTemplateId, setScheduleLockTemplateId] = useState(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleDeleteTarget, setScheduleDeleteTarget] = useState(null);
  const [scheduleDeleting, setScheduleDeleting] = useState(false);

  const [scheduleView, setScheduleView] = useState("table");
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(isMdUp);
  const [completingScheduleId, setCompletingScheduleId] = useState(null);

  useEffect(() => {
    setTemplateDrawerOpen(isMdUp);
  }, [isMdUp]);

  const filteredRows = useMemo(() => {
    const q = filterCode.trim().toLowerCase();
    return rows.filter((r) => {
      if (
        filterActive !== "all" &&
        Boolean(r.is_active) !== (filterActive === "true")
      )
        return false;
      const code = (r.template_code || "").toLowerCase();
      const subj = (r.subject || "").toLowerCase();
      return code.includes(q) || subj.includes(q);
    });
  }, [rows, filterCode, filterActive]);

  const timelineBuckets = useMemo(() => {
    return scheduleBuckets(schedules);
  }, [schedules]);

  const quickCompleteTarget = useMemo(() => {
    const { live, upcoming } = timelineBuckets;
    if (live.length > 0) return { sch: live[0], hint: "live" };
    if (upcoming.length > 0) return { sch: upcoming[0], hint: "upcoming" };
    return null;
  }, [timelineBuckets]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const tplRes = await listPatchingTemplates();
      setRows(extractList(tplRes));
    } catch (e) {
      setSnack({
        severity: "error",
        message: e?.message || "Failed to load templates",
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [listPatchingTemplates]);

  const loadSchedules = useCallback(async () => {
    const requestId = ++scheduleRequestId.current;
    setSchedulesLoading(true);
    setSchedulesError("");
    try {
      const schRes = await listPatchingSchedules({
        page: schedulePage + 1,
        limit: scheduleRowsPerPage,
        ...(scheduleStatus !== "all" ? { status: scheduleStatus } : {}),
      });
      if (requestId !== scheduleRequestId.current) return;

      const paginated = extractPaginatedSchedules(schRes);
      const maxPage = Math.max(
        0,
        Math.ceil(paginated.total / scheduleRowsPerPage) - 1
      );
      if (schedulePage > maxPage) {
        setScheduleTotal(paginated.total);
        setSchedulePage(maxPage);
        return;
      }

      setSchedules(paginated.items);
      setScheduleTotal(paginated.total);
    } catch (e) {
      if (requestId !== scheduleRequestId.current) return;
      setSchedulesError(e?.message || "Failed to load schedules");
      setSchedules([]);
    } finally {
      if (requestId === scheduleRequestId.current) setSchedulesLoading(false);
    }
  }, [
    listPatchingSchedules,
    schedulePage,
    scheduleRowsPerPage,
    scheduleStatus,
  ]);

  const loadTemplatesAndSchedules = useCallback(async () => {
    await Promise.all([loadTemplates(), loadSchedules()]);
  }, [loadTemplates, loadSchedules]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    loadSchedules();
    return () => {
      scheduleRequestId.current += 1;
    };
  }, [loadSchedules]);

  const openCreate = () => {
    setFormMode("create");
    setEditingTemplate(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setFormMode("edit");
    setEditingTemplate(row);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (submitting) return;
    setFormOpen(false);
    setEditingTemplate(null);
  };

  const handleFormSubmit = async ({ t_id, payload }) => {
    setSubmitting(true);
    try {
      if (t_id) {
        const res = await updatePatchingTemplate(t_id, payload);
        setSnack({
          severity: "success",
          message: res?.message || "Template updated",
        });
      } else {
        const res = await createPatchingTemplate(payload);
        setSnack({
          severity: "success",
          message: res?.message || "Template created",
        });
      }
      setFormOpen(false);
      setEditingTemplate(null);
      await loadTemplatesAndSchedules();
    } catch (e) {
      setSnack({
        severity: "error",
        message: e?.message || "Request failed",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.t_id) return;
    setDeleting(true);
    try {
      const res = await deletePatchingTemplate(deleteTarget.t_id);
      setSnack({
        severity: "success",
        message: res?.message || "Template deleted",
      });
      setDeleteTarget(null);
      await loadTemplatesAndSchedules();
    } catch (e) {
      setSnack({
        severity: "error",
        message: e?.message || "Delete failed",
      });
    } finally {
      setDeleting(false);
    }
  };

  const closeScheduleForm = () => {
    if (scheduleSubmitting) return;
    setScheduleFormOpen(false);
    setEditingSchedule(null);
    setScheduleLockTemplateId(null);
  };

  const openScheduleCreate = () => {
    setScheduleMode("create");
    setEditingSchedule(null);
    setScheduleLockTemplateId(null);
    setScheduleFormOpen(true);
  };

  const openScheduleFromTemplate = (template) => {
    setScheduleMode("create");
    setEditingSchedule(null);
    setScheduleLockTemplateId(template.t_id);
    setScheduleFormOpen(true);
  };

  const openScheduleEdit = (sch) => {
    setScheduleMode("edit");
    setEditingSchedule(sch);
    setScheduleLockTemplateId(null);
    setScheduleFormOpen(true);
  };

  const handleScheduleFormSubmit = async ({ s_id, payload }) => {
    setScheduleSubmitting(true);
    try {
      if (s_id) {
        const res = await updatePatchingSchedule(s_id, payload);
        setSnack({
          severity: "success",
          message: res?.message || "Schedule updated",
        });
      } else {
        const res = await createPatchingSchedule(payload);
        setSnack({
          severity: "success",
          message: res?.message || "Schedule created",
        });
      }
      setScheduleFormOpen(false);
      setEditingSchedule(null);
      setScheduleLockTemplateId(null);
      await loadSchedules();
    } catch (e) {
      setSnack({
        severity: "error",
        message: e?.message || "Schedule request failed",
      });
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const confirmScheduleDelete = async () => {
    if (!scheduleDeleteTarget?.s_id) return;
    setScheduleDeleting(true);
    try {
      const res = await deletePatchingSchedule(scheduleDeleteTarget.s_id);
      setSnack({
        severity: "success",
        message: res?.message || "Schedule deleted",
      });
      setScheduleDeleteTarget(null);
      await loadSchedules();
    } catch (e) {
      setSnack({
        severity: "error",
        message: e?.message || "Delete failed",
      });
    } finally {
      setScheduleDeleting(false);
    }
  };

  const markScheduleComplete = async (sch) => {
    if (!sch?.s_id || completingScheduleId) return;
    setCompletingScheduleId(sch.s_id);
    try {
      const res = await updatePatchingSchedule(sch.s_id, {
        status: "completed",
      });
      setSnack({
        severity: "success",
        message: res?.message || "Schedule marked complete",
      });
      await loadSchedules();
    } catch (e) {
      setSnack({
        severity: "error",
        message: e?.message || "Could not update schedule",
      });
    } finally {
      setCompletingScheduleId(null);
    }
  };

  const headCellSx = {
    fontWeight: 600,
    color: "text.secondary",
    borderBottom: "1px solid",
    borderColor: "divider",
    py: 1.5,
    bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
  };

  const bodyCellSx = {
    py: 1.5,
    borderColor: "divider",
  };

  const canMarkComplete = (sch) =>
    sch && (sch.status === "planned" || sch.status === "in_progress");

  const renderScheduleRowActions = (sch) => (
    <>
      {canMarkComplete(sch) ? (
        <Tooltip title='Mark complete'>
          <span>
            <IconButton
              aria-label='Mark schedule complete'
              size='small'
              color='success'
              disabled={Boolean(completingScheduleId)}
              onClick={() => markScheduleComplete(sch)}
            >
              <TaskAltIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      <Tooltip title='Edit'>
        <IconButton
          aria-label='Edit schedule'
          size='small'
          onClick={() => openScheduleEdit(sch)}
        >
          <EditIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip title='Delete'>
        <IconButton
          size='small'
          color='error'
          aria-label='Delete schedule'
          onClick={() => setScheduleDeleteTarget(sch)}
        >
          <DeleteOutlineIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    </>
  );

  const templateLabel = (sch) => {
    const tpl = sch.template;
    return tpl ? `${tpl.template_code} v${tpl.version}` : sch.template_id;
  };

  const renderTimelineSection = (title, items, accent) => (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant='overline'
        fontWeight={700}
        sx={{
          color: accent,
          letterSpacing: 1,
          display: "block",
          mb: 1.5,
          pl: 0.5,
        }}
      >
        {title} ({items.length})
      </Typography>
      {items.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ pl: 0.5 }}>
          None
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {items.map((sch) => (
            <PaperCustom
              key={sch.s_id}
              sx={{
                p: 2,
                borderLeft: "4px solid",
                borderLeftColor: accent,
              }}
            >
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='flex-start'
                spacing={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant='subtitle2'
                    fontWeight={600}
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {templateLabel(sch)}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {formatScheduleDate(sch.scheduled_start_time)}
                    {sch.scheduled_end_time
                      ? ` → ${formatScheduleDate(sch.scheduled_end_time)}`
                      : ""}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <ScheduleStatus status={sch.status} />
                  </Box>
                </Box>
                <Stack direction='row' alignItems='center' flexShrink={0}>
                  {renderScheduleRowActions(sch)}
                </Stack>
              </Stack>
            </PaperCustom>
          ))}
        </Stack>
      )}
    </Box>
  );

  const templateDrawer = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
          <MenuBookIcon color='primary' fontSize='small' />
          <Typography variant='subtitle1' fontWeight={600} sx={{ flex: 1 }}>
            Template library
          </Typography>
          <Chip label={rows.length} size='small' />
          {!isMdUp && (
            <IconButton
              aria-label='Close template library'
              size='small'
              onClick={() => setTemplateDrawerOpen(false)}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          )}
        </Stack>
        <Typography variant='caption' color='text.secondary'>
          Choose a template to create a notification.
        </Typography>
        <Button
          size='small'
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ mt: 1, textTransform: "none" }}
        >
          New template
        </Button>
      </Box>
      <Box sx={{ p: 2 }}>
        <TextField
          placeholder='Search templates'
          size='small'
          fullWidth
          value={filterCode}
          onChange={(e) => setFilterCode(e.target.value)}
          inputProps={{ "aria-label": "Search templates" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon color='action' sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction='row' spacing={0.75} sx={{ mt: 1.5 }}>
          {["all", "true", "false"].map((v) => (
            <Chip
              key={v}
              size='small'
              label={v === "all" ? "All" : v === "true" ? "Active" : "Inactive"}
              clickable
              color={filterActive === v ? "primary" : "default"}
              variant={filterActive === v ? "filled" : "outlined"}
              onClick={() => setFilterActive(v)}
            />
          ))}
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflow: "auto", maxHeight: { md: 580 } }}>
        {loading ? (
          <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
            Loading templates...
          </Typography>
        ) : filteredRows.length === 0 ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ p: 3, textAlign: "center" }}
          >
            {rows.length === 0 ? "No templates yet" : "No matching templates"}
          </Typography>
        ) : (
          <List disablePadding aria-label='Patching templates'>
            {filteredRows.map((t) => (
              <ListItem
                key={t.t_id}
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "block",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{ overflowWrap: "anywhere", flex: 1 }}
                      >
                        {t.template_code}
                      </Typography>
                      <Chip
                        label={`v${t.version}`}
                        size='small'
                        sx={{ height: 22 }}
                      />
                    </Stack>
                  }
                  secondary={
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        display: "block",
                        mt: 0.5,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {truncate(t.subject, 80)}
                    </Typography>
                  }
                />
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  sx={{ mt: 1 }}
                >
                  <Chip
                    label={t.is_active ? "Active" : "Inactive"}
                    size='small'
                    color={t.is_active ? "success" : "default"}
                    variant='outlined'
                    sx={{ height: 22 }}
                  />
                  <Stack direction='row'>
                    <Tooltip
                      title={
                        t.is_active
                          ? "Create notification"
                          : "Activate to schedule"
                      }
                    >
                      <span>
                        <IconButton
                          aria-label={`Schedule ${t.template_code}`}
                          size='small'
                          color='primary'
                          onClick={() => openScheduleFromTemplate(t)}
                          disabled={!t.is_active}
                        >
                          <SendIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='Edit template'>
                      <IconButton
                        aria-label={`Edit ${t.template_code}`}
                        size='small'
                        onClick={() => openEdit(t)}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete template'>
                      <IconButton
                        aria-label={`Delete ${t.template_code}`}
                        size='small'
                        color='error'
                        onClick={() => setDeleteTarget(t)}
                      >
                        <DeleteOutlineIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <PaperCustom sx={{ p: { xs: 2, sm: 2.5 }, mb: 2.5 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent='space-between'
          spacing={2}
        >
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Box
              sx={{
                p: 1.25,
                display: "flex",
                borderRadius: 2,
                color: "primary.main",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              }}
            >
              <BuildIcon />
            </Box>
            <Box>
              <Typography variant='h6' fontWeight={700}>
                Patching Center
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Manage patching windows and notification templates.
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            {!isMdUp && (
              <Button
                variant='outlined'
                startIcon={<MenuBookIcon />}
                onClick={() => setTemplateDrawerOpen(true)}
                sx={{ textTransform: "none" }}
              >
                Templates
              </Button>
            )}
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={openScheduleCreate}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              New notification
            </Button>
          </Stack>
        </Stack>
      </PaperCustom>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: `minmax(0, 1fr) ${TEMPLATE_DRAWER_WIDTH}px`,
          },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Box component='main' sx={{ minWidth: 0 }}>
          {!schedulesLoading && !schedulesError && quickCompleteTarget && (
            <PaperCustom
              sx={{
                p: 2,
                mb: 2,
                borderColor: (t) => alpha(t.palette.primary.main, 0.25),
                bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent='space-between'
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Quick close-out on this page
                  </Typography>
                  <Typography
                    variant='subtitle2'
                    fontWeight={600}
                    sx={{ my: 0.5, overflowWrap: "anywhere" }}
                  >
                    {templateLabel(quickCompleteTarget.sch)}
                  </Typography>
                  <Stack
                    direction='row'
                    spacing={1}
                    useFlexGap
                    flexWrap='wrap'
                    alignItems='center'
                  >
                    <ScheduleStatus status={quickCompleteTarget.sch.status} />
                    <Typography variant='caption' color='text.secondary'>
                      {formatScheduleDate(
                        quickCompleteTarget.sch.scheduled_start_time
                      )}
                    </Typography>
                  </Stack>
                </Box>
                <Button
                  size='small'
                  variant='outlined'
                  color='success'
                  startIcon={<TaskAltIcon />}
                  onClick={() => markScheduleComplete(quickCompleteTarget.sch)}
                  disabled={Boolean(completingScheduleId)}
                  sx={{ flexShrink: 0, textTransform: "none" }}
                >
                  Mark complete
                </Button>
              </Stack>
            </PaperCustom>
          )}
          <PaperCustom sx={{ overflow: "hidden" }}>
            <Box
              sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography
                  variant='subtitle1'
                  fontWeight={600}
                  sx={{ flex: 1 }}
                >
                  Schedules
                </Typography>
                <Tooltip title='Refresh schedules'>
                  <span>
                    <IconButton
                      aria-label='Refresh schedules'
                      size='small'
                      disabled={schedulesLoading}
                      onClick={loadSchedules}
                    >
                      <RefreshIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Typography variant='caption' color='text.secondary'>
                Latest scheduled starts first. Browse older windows using the
                page controls.
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent='space-between'
                spacing={1.5}
                sx={{ mt: 2 }}
              >
                <TextField
                  select
                  size='small'
                  label='Status'
                  value={scheduleStatus}
                  disabled={schedulesLoading}
                  onChange={(e) => {
                    setScheduleStatus(e.target.value);
                    setSchedulePage(0);
                  }}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value='all'>All statuses</MenuItem>
                  {Object.entries(SCHEDULE_STATUSES).map(([value, option]) => (
                    <MenuItem key={value} value={value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <ToggleButtonGroup
                  exclusive
                  size='small'
                  value={scheduleView}
                  onChange={(_, v) => v && setScheduleView(v)}
                  aria-label='Schedule view'
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      px: 1.5,
                    },
                  }}
                >
                  <ToggleButton value='table' aria-label='Table'>
                    <TableRowsIcon sx={{ mr: 0.75, fontSize: 18 }} />
                    Table
                  </ToggleButton>
                  <ToggleButton value='timeline' aria-label='Timeline'>
                    <ViewTimelineIcon sx={{ mr: 0.75, fontSize: 18 }} />
                    Timeline
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Box>
            <Box aria-busy={schedulesLoading}>
              {schedulesLoading ? (
                <Box role='status' sx={{ py: 8, textAlign: "center" }}>
                  <Typography color='text.secondary'>
                    Loading schedules...
                  </Typography>
                </Box>
              ) : schedulesError ? (
                <Alert
                  severity='error'
                  sx={{ m: 2 }}
                  action={
                    <Button
                      color='inherit'
                      size='small'
                      onClick={loadSchedules}
                    >
                      Retry
                    </Button>
                  }
                >
                  {schedulesError}
                </Alert>
              ) : schedules.length === 0 ? (
                <Box sx={{ px: 3, py: 6, textAlign: "center" }}>
                  <ViewTimelineIcon
                    sx={{ fontSize: 36, color: "text.disabled", mb: 1 }}
                  />
                  <Typography variant='subtitle2' fontWeight={600}>
                    {scheduleStatus === "all"
                      ? "No schedules yet"
                      : "No matching schedules"}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 0.5 }}
                  >
                    {scheduleStatus === "all"
                      ? "Create a notification to schedule a patching window."
                      : "Choose another status to browse your schedules."}
                  </Typography>
                </Box>
              ) : scheduleView === "table" ? (
                <TableContainer>
                  <Table
                    size='small'
                    aria-label='Patching schedules'
                    sx={{ minWidth: 560 }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Template</TableCell>
                        <TableCell sx={headCellSx}>Patching window</TableCell>
                        <TableCell sx={headCellSx}>Status</TableCell>
                        <TableCell sx={headCellSx} align='right'>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {schedules.map((sch) => (
                        <TableRow key={sch.s_id} hover>
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              maxWidth: 240,
                              overflowWrap: "anywhere",
                            }}
                          >
                            <Typography variant='body2' fontWeight={600}>
                              {templateLabel(sch)}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              {truncate(sch.template?.subject, 72)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{ ...bodyCellSx, whiteSpace: "nowrap" }}
                          >
                            <Typography variant='body2'>
                              {formatScheduleDate(sch.scheduled_start_time)}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              {sch.scheduled_end_time
                                ? `Until ${formatScheduleDate(sch.scheduled_end_time)}`
                                : "No end time"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <ScheduleStatus status={sch.status} />
                          </TableCell>
                          <TableCell
                            sx={{ ...bodyCellSx, whiteSpace: "nowrap" }}
                            align='right'
                          >
                            {renderScheduleRowActions(sch)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: "block", mb: 2 }}
                  >
                    Timeline for this page
                  </Typography>
                  {timelineBuckets.live.length > 0 &&
                    renderTimelineSection(
                      "In progress",
                      timelineBuckets.live,
                      theme.palette.warning.main
                    )}
                  {timelineBuckets.upcoming.length > 0 &&
                    renderTimelineSection(
                      "Upcoming",
                      timelineBuckets.upcoming,
                      theme.palette.primary.main
                    )}
                  {timelineBuckets.closed.length > 0 &&
                    renderTimelineSection(
                      "Completed / cancelled / past",
                      timelineBuckets.closed,
                      theme.palette.text.secondary
                    )}
                </Box>
              )}
            </Box>
            <TablePagination
              component='div'
              count={scheduleTotal}
              page={schedulePage}
              onPageChange={(_, p) => setSchedulePage(p)}
              rowsPerPage={scheduleRowsPerPage}
              onRowsPerPageChange={(e) => {
                setScheduleRowsPerPage(parseInt(e.target.value, 10));
                setSchedulePage(0);
              }}
              rowsPerPageOptions={SCHEDULE_ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage='Show'
              SelectProps={{
                disabled: schedulesLoading || Boolean(schedulesError),
              }}
              backIconButtonProps={{
                disabled:
                  schedulesLoading ||
                  Boolean(schedulesError) ||
                  schedulePage === 0,
              }}
              nextIconButtonProps={{
                disabled:
                  schedulesLoading ||
                  Boolean(schedulesError) ||
                  (schedulePage + 1) * scheduleRowsPerPage >= scheduleTotal,
              }}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": { px: 1.5, flexWrap: "wrap" },
                "& .MuiTablePagination-spacer": { display: "none" },
                "& .MuiTablePagination-actions": { ml: "auto" },
              }}
            />
          </PaperCustom>
        </Box>
        {isMdUp && (
          <PaperCustom component='aside' sx={{ overflow: "hidden" }}>
            {templateDrawer}
          </PaperCustom>
        )}
      </Box>
      {!isMdUp && (
        <Drawer
          anchor='right'
          open={templateDrawerOpen}
          onClose={() => setTemplateDrawerOpen(false)}
          PaperProps={{
            sx: { width: TEMPLATE_DRAWER_WIDTH, maxWidth: "100%" },
          }}
        >
          {templateDrawer}
        </Drawer>
      )}

      <PatchingTemplateFormDialog
        open={formOpen}
        mode={formMode}
        template={editingTemplate}
        onClose={closeForm}
        onSubmit={handleFormSubmit}
        submitting={submitting}
      />

      <PatchingScheduleFormDialog
        open={scheduleFormOpen}
        mode={scheduleMode}
        schedule={editingSchedule}
        templates={rows}
        lockTemplateId={scheduleLockTemplateId}
        onClose={closeScheduleForm}
        onSubmit={handleScheduleFormSubmit}
        submitting={scheduleSubmitting}
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack(null)}
          severity={snack?.severity || "info"}
          variant='filled'
          sx={{ width: "100%" }}
        >
          {snack?.message}
        </Alert>
      </Snackbar>

      <DeleteTemplateDialog
        deleteTarget={deleteTarget}
        setDeleteTarget={setDeleteTarget}
        deleting={deleting}
        confirmDelete={confirmDelete}
      />

      <DeleteScheduleDialog
        scheduleDeleteTarget={scheduleDeleteTarget}
        setScheduleDeleteTarget={setScheduleDeleteTarget}
        scheduleDeleting={scheduleDeleting}
        confirmScheduleDelete={confirmScheduleDelete}
      />
    </Box>
  );
};

export default PatchingCenterConsole;
